import { Box, Button, Slider } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import Player from "video.js/dist/types/player";
import { API_BASE_URL } from "~/config";
import { useSlider } from "~/utils/hooks/useSlider";
import { RecordedVideo } from "./RecordedVideo";
import { CustomTime } from "./VideoTimesList";

export type VideoClipProps = {
  cameraId: number;
  selectedTime: CustomTime | undefined;
};

export type VideoClipOptions = {
  dateTime: Date;
  durationSeconds: number;
  sliderValue: number[];
};

export const dayInSeconds = 86400;

export const sliderGetAriaValueText = (
  value: number,
  startTime: Date | undefined,
  format = "HH:mm",
) => {
  const labelTime = dayjs(startTime).add(value, "second");

  if (value / dayInSeconds === 1 || dayjs(startTime).day() !== labelTime.day())
    return "24:00";
  return `${labelTime.format(format)}`;
};

export const VideoClip = ({
  cameraId,
  selectedTime,
}: VideoClipProps): JSX.Element => {
  const { t } = useTranslation("");

  const sliderValueText = React.useCallback(
    (value: number) => {
      return sliderGetAriaValueText(value, selectedTime?.startInclusive);
    },
    [selectedTime?.startInclusive],
  );

  const [videoObjectOptions, setVideoObjectOptions] = React.useState<
    VideoClipOptions | undefined
  >();

  // const { recordedStream } = useRecordedHlsStream({
  //   cameraId,

  //   ...videoObjectOptions,
  //   // enabled: false,
  // });

  // seconds
  const maxTime = React.useMemo(
    () =>
      dayjs(selectedTime?.endExclusive).diff(
        dayjs(selectedTime?.startInclusive),
      ) / 1000,
    [selectedTime?.endExclusive, selectedTime?.startInclusive],
  );

  const initialVal = maxTime ? [0, maxTime] : [];

  const { setSliderValue, sliderValue } = useSlider({
    initialValue: initialVal,
  });

  React.useEffect(() => {
    setSliderValue([0, maxTime]);
  }, [maxTime, setSliderValue]);

  const handleChange = React.useCallback(
    (_event: Event, newValue: number | number[]) => {
      setSliderValue(newValue as number[]);
    },
    [setSliderValue],
  );

  const handleGetStream = React.useCallback(() => {
    setVideoObjectOptions({
      dateTime: dayjs(selectedTime?.startInclusive)
        .add(sliderValue[0], "seconds")
        .startOf("minute")
        .toDate(),
      durationSeconds: sliderValue[1] - sliderValue[0],
      sliderValue,
    });

    //   void refetch();
  }, [selectedTime?.startInclusive, sliderValue]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayerReady = React.useCallback((_player: Player) => {
    // playerRef.current = player;
    // // You can handle player events here, for example:
    // player.on("waiting", () => {
    //   videojs.log("player is waiting");
    // });
    // player.on("dispose", () => {
    //   videojs.log("player will dispose");
    // });
  }, []);

  const videoJsOptions = React.useMemo(() => {
    if (!videoObjectOptions) return {};
    const duration = Math.round(videoObjectOptions.durationSeconds);
    const dateUrl = encodeURIComponent(videoObjectOptions.dateTime.toJSON());
    const src = `${API_BASE_URL}/api/Camera/${cameraId}/RecordedHlsStream?dateTime=${dateUrl}&durationSeconds=${duration}`;

    return {
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      sources: [
        {
          src,
          type: "application/x-mpegURL",
        },
      ],
    };
  }, [cameraId, videoObjectOptions]);

  const endTime = React.useMemo(() => {
    return dayjs(videoObjectOptions?.dateTime)
      .add(videoObjectOptions?.sliderValue?.[1] ?? 0, "seconds")
      .toDate();
  }, [videoObjectOptions]);

  return (
    <Box>
      {selectedTime && (
        <Box sx={{ paddingY: "15px" }}>
          <Slider
            value={sliderValue}
            onChange={handleChange}
            valueLabelDisplay="on"
            getAriaValueText={sliderValueText}
            valueLabelFormat={sliderValueText}
            max={maxTime}
            step={60}
          />
          <Button
            sx={{ marginLeft: "auto", display: "flex" }}
            variant="contained"
            onClick={handleGetStream}
          >
            {t("recordedStream:searchStream")}
          </Button>
        </Box>
      )}

      {videoObjectOptions && (
        <Box>
          <RecordedVideo
            cameraId={cameraId}
            options={videoJsOptions}
            onReady={handlePlayerReady}
            videoSliderValue={videoObjectOptions.sliderValue}
            startDateTime={videoObjectOptions.dateTime}
            endTime={endTime}
            allowClipping
          />
        </Box>
      )}
    </Box>
  );
};
